// TraceabilityPage.jsx

import { Input, Tooltip } from "antd";
import { EnvironmentOutlined, WarningOutlined } from "@ant-design/icons";
import ReusableTable from "../../Reuseable/ReusableTable/ReusableTable"; // adjust path as needed
import PanelCard from "../DashboardPage/components/PanelCard";
import "./TraceabilityPage.scss";
import { useEffect, useMemo, useState } from "react";
import moment from "moment";
import AgentDetailsModal from "../../components/TraceabilityComp/AgentDetailsModal/AgentDetailsModal";
import { ReactComponent as SearchICon } from "../../assets/Icons/SearchIcon.svg";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useSelector } from "react-redux";
import { GeoTagging } from "../../api/GeoTagging";
import { Skeleton } from "antd";

const EMPTY = "—";

const initialsOf = (name) =>
  String(name || "")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((word) => word[0])
    .join("")
    .toUpperCase();

const TraceabilityPage = () => {
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const skeletonData = Array(5).fill({});
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [geoTable, setGeoTable] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");

  const { token } = useSelector((state) => state.auth);

  // const handleViewClick = (record) => {
  //   setSelectedAgent(record);
  //   setModalOpen(true);
  // };

  const navigate = useNavigate();

  const fetchGeoTable = async (payload = {}) => {
    if (!token) {
      setError("Authentication token not found");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await GeoTagging(token);

      setGeoTable(response.data || []);
    } catch (error) {

      setError(
        error.response?.data?.message ||
          error.message ||
          "Failed to fetch geo table data"
      );
      setGeoTable([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGeoTable();
  }, [token]);

  const handleViewClick = (data) => {
    navigate("/geo-tagging/agent-details", { state: data });
  };

  const agents = useMemo(
    () => geoTable?.results?.field_agents || [],
    [geoTable],
  );

  /* agency/field-agents/ takes no query params, so the whole list arrives in
     one response. The search box had no handler at all and the pager counted
     against `count` while never asking for another page - both are matched
     against the rows we already hold. */
  const filteredAgents = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return agents;
    return agents.filter((agent) =>
      ["name", "role", "mobile_no", "address", "dra_reg_no"].some((key) =>
        String(agent?.[key] ?? "")
          .toLowerCase()
          .includes(term),
      ),
    );
  }, [agents, search]);

  const pagedAgents = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredAgents.slice(start, start + pageSize);
  }, [filteredAgents, currentPage, pageSize]);

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (text) =>
        loading ? (
          <Skeleton.Input size="small" active />
        ) : (
          <span className="geoAgent">
            <span className="geoAgent__avatar" aria-hidden="true">
              {initialsOf(text) || "NA"}
            </span>
            <span className="geoAgent__name">{text || EMPTY}</span>
          </span>
        ),
    },
    {
      title: "Agent",
      dataIndex: "role",
      key: "role",
      render: (text) =>
        loading ? (
          <Skeleton.Input size="small" active />
        ) : text ? (
          <span className="geoRoleChip">{text}</span>
        ) : (
          EMPTY
        ),
    },
    {
      title: "Mobile",
      dataIndex: "mobile_no",
      key: "mobile_no",
      render: (text) =>
        loading ? (
          <Skeleton.Input size="small" active />
        ) : (
          <span className="geoMono">{text || EMPTY}</span>
        ),
    },
    {
      title: "Address",
      dataIndex: "address",
      key: "address",
      ellipsis: true,
      width: 260,
      render: (text) =>
        loading ? (
          <Skeleton.Input size="small" active />
        ) : text ? (
          /* the column was already ellipsised, but with no way to read the
             rest of it */
          <Tooltip title={text} mouseEnterDelay={0.35}>
            <span className="geoAddress">{text}</span>
          </Tooltip>
        ) : (
          EMPTY
        ),
    },
    {
      title: "Last Active",
      dataIndex: "last_active",
      key: "last_active",
      render: (text) => {
        if (loading) return <Skeleton.Input size="small" active />;
        if (!text) return EMPTY;
        const when = moment(text);
        return (
          <span className="geoLastActive">
            <span className="geoLastActive__value">
              {when.isValid() ? when.format("DD MMM YYYY, hh:mm A") : text}
            </span>
            {when.isValid() && (
              <span className="geoLastActive__ago">{when.fromNow()}</span>
            )}
          </span>
        );
      },
    },
    {
      title: "Dra Reg. No.",
      dataIndex: "dra_reg_no",
      key: "dra_reg_no",
      render: (text) =>
        loading ? (
          <Skeleton.Input size="small" active />
        ) : (
          <span className="geoMono">{text || EMPTY}</span>
        ),
    },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      width: 130,
      render: (_, record) =>
        loading ? (
          <Skeleton.Input size="small" active />
        ) : (
          <button
            type="button"
            className="viewBtn"
            onClick={() => handleViewClick(record)}
          >
            <EnvironmentOutlined />
            View Details
          </button>
        ),
    },
  ];

  return (
    <div className="traceabilityPage">
      <PanelCard
        title="Field Agent List"
        icon={<EnvironmentOutlined />}
        accent="brand"
        className="traceabilityCard"
        actions={
          <div className="traceabilityTools">
            {!loading && (
              <span className="traceabilityCount">
                {filteredAgents.length}
                {search.trim() && agents.length !== filteredAgents.length
                  ? ` of ${agents.length}`
                  : ""}
              </span>
            )}
            <Input
              placeholder="Search agents"
              allowClear
              className="searchInput"
              prefix={<SearchICon />}
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setCurrentPage(1);
              }}
            />
          </div>
        }
      >
        {/* was red inline-styled text floating above the table */}
        {error && (
          <div className="traceabilityError" role="alert">
            <WarningOutlined />
            <span>{error}</span>
          </div>
        )}

        <ReusableTable
          columns={columns}
          dataSource={loading ? skeletonData : pagedAgents}
          pageSize={pageSize}
          setPageSize={setPageSize}
          CurrentPageNo={currentPage}
          totalItems={loading ? 0 : filteredAgents.length}
          triggerChange={(page, size) => {
            setCurrentPage(page);
            setPageSize(size);
          }}
        />
      </PanelCard>
      {/* <AgentDetailsModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        agent={selectedAgent}
      /> */}
    </div>
  );
};

export default TraceabilityPage;
